import React, { useState, useEffect } from 'react';
import { X, Send, Home, Building, Ruler, HelpCircle, MessageCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inquiryOptions = [
  { 
    id: 'rent', 
    label: 'Looking to Rent', 
    icon: Home,
    description: 'Find your perfect rental property'
  },
  { 
    id: 'buy', 
    label: 'Looking to Buy', 
    icon: Building,
    description: 'Purchase a property'
  },
  { 
    id: 'landlord', 
    label: 'I\'m a Landlord', 
    icon: Home,
    description: 'List or manage my property'
  },
  { 
    id: 'architecture', 
    label: 'Architecture Services', 
    icon: Ruler,
    description: 'Extensions, renovations & designs'
  },
  { 
    id: 'valuation', 
    label: 'Property Valuation', 
    icon: Building,
    description: 'Get a free valuation'
  },
  { 
    id: 'other', 
    label: 'Other Enquiry', 
    icon: HelpCircle,
    description: 'General questions'
  }
];

export const FloatingWhatsApp = () => {
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState('');
  const [name, setName] = useState('');
  const [customMessage, setCustomMessage] = useState('');

  useEffect(() => {
    const fetchWhatsAppNumber = async () => {
      try {
        const response = await fetch(`${API_URL}/api/config/whatsapp`);
        if (response.ok) {
          const data = await response.json();
          setWhatsappNumber(data.whatsapp_number);
        }
      } catch (error) {
        console.error('Error fetching WhatsApp config:', error);
      }
    };
    fetchWhatsAppNumber();
  }, []);

  const handleButtonClick = () => {
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
    setSelectedInquiry('');
    setName('');
    setCustomMessage('');
  };

  const openWhatsApp = () => {
    if (!whatsappNumber || !selectedInquiry) return;
    
    const selectedOption = inquiryOptions.find(opt => opt.id === selectedInquiry);
    let message = `Hi Harry & Jacobs!\n\n`;
    
    if (name) {
      message += `My name is ${name}.\n\n`;
    }
    
    message += `I'm contacting you about: ${selectedOption?.label}\n`;
    
    if (customMessage) {
      message += `\nAdditional details:\n${customMessage}`;
    }
    
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');
    handleClose();
  };

  if (!whatsappNumber) return null;

  return (
    <>
      {/* Pre-chat Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
          />
          
          {/* Form Card */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden animate-in slide-in-from-bottom duration-300">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_harry-and-jacobs/artifacts/7nr8z83x_Insta%20Profile%20Pic.jpg"
                  alt="Harry & Jacobs"
                  className="w-10 h-10 rounded-full"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold text-lg">Harry & Jacobs</h3>
                <p className="text-green-100 text-sm">Usually replies within minutes</p>
              </div>
              <button 
                onClick={handleClose}
                className="text-white/80 hover:text-white p-1"
                data-testid="whatsapp-form-close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Form Content */}
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {/* Name Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Name (optional)
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  data-testid="whatsapp-name-input"
                />
              </div>

              {/* Inquiry Type Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What can we help you with? <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {inquiryOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.id}
                        onClick={() => setSelectedInquiry(option.id)}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          selectedInquiry === option.id
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                        }`}
                        data-testid={`whatsapp-option-${option.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={`w-4 h-4 ${selectedInquiry === option.id ? 'text-green-600' : 'text-gray-500'}`} />
                          <span className={`text-sm font-medium ${selectedInquiry === option.id ? 'text-green-700' : 'text-gray-700'}`}>
                            {option.label}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">{option.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Additional Message */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional details (optional)
                </label>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Tell us more about your enquiry..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                  data-testid="whatsapp-message-input"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50">
              <button
                onClick={openWhatsApp}
                disabled={!selectedInquiry}
                className={`w-full py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                  selectedInquiry
                    ? 'bg-green-500 hover:bg-green-600 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                data-testid="whatsapp-send-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Start Chat on WhatsApp
              </button>
              <p className="text-xs text-gray-500 text-center mt-2">
                You'll be redirected to WhatsApp
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={handleButtonClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 transition-all duration-300 group"
        aria-label="Contact us on WhatsApp"
        data-testid="floating-whatsapp-btn"
      >
        {/* Tooltip */}
        <div className={`
          bg-white px-4 py-2 rounded-full shadow-lg 
          transition-all duration-300 flex items-center gap-2
          ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4 pointer-events-none'}
        `}>
          <img 
            src="https://customer-assets.emergentagent.com/job_harry-and-jacobs/artifacts/7nr8z83x_Insta%20Profile%20Pic.jpg"
            alt="Harry & Jacobs"
            className="w-6 h-6 rounded-full"
          />
          <span className="text-[#404040] font-medium text-sm whitespace-nowrap">Chat with us!</span>
        </div>

        {/* WhatsApp Button */}
        <div className="relative">
          {/* Pulse animation */}
          <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-25"></div>
          
          {/* Main button */}
          <div className="relative bg-green-500 hover:bg-green-600 p-4 rounded-full shadow-lg transition-transform hover:scale-110">
            <svg 
              className="w-7 h-7 text-white" 
              viewBox="0 0 24 24" 
              fill="currentColor"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </div>
        </div>
      </button>
    </>
  );
};
